import type { AppointmentService } from './data/mock-appointments';

/**
 * The entity forms attached to an appointment type, in the shape the shared
 * booking checkout takes (`.classForms`).
 *
 * The embed used to build this from `AppointmentTypeDto.formId` alone, which
 * does not work on live data: the DTO's constructor migrates a legacy `formId`
 * into `forms` (typed as a waiver) and then sets `formId = 0`. So `formId`
 * reads 0 for every appointment type the API serves — and 0 is not `null`, so
 * the old check passed `[{ formId: 0 }]` down to a checkout that fetches every
 * declared form and fails CLOSED when one can't be loaded. The org's real
 * forms were never fetched, and the booking button sat disabled behind a
 * "couldn't load forms" banner even for types carrying no form at all.
 *
 * `forms` is therefore the source of truth here, filtered by `!!formId` exactly
 * like the class and event feeds do, with the real `type` preserved so a waiver
 * gets the consent block and everything else the guest intake step.
 */
export interface AppointmentFormRef {
  formId: number;
  type: string;
  requirement?: string;
}

export function serviceForms(
  service: Pick<AppointmentService, 'forms' | 'formId'>,
): AppointmentFormRef[] {
  const forms = (service.forms ?? []).filter((f) => !!f.formId);
  if (forms.length) {
    return forms.map((f) => ({
      formId: f.formId,
      type: f.type || 'other',
      requirement: f.requirement,
    }));
  }
  // Legacy fallback for a type the API served without migrating — mirrors the
  // server's own (`getRequiredFormsForAppointmentType`, and the DTO migration),
  // which reads a bare `formId` as a required waiver.
  if (service.formId && service.formId > 0) {
    return [{ formId: service.formId, type: 'waiver' }];
  }
  return [];
}
